/**
 * AuthDirectives.ts
 * Declarative DOM Authorization Directives for ApnaGuru Story Animation Engine (ASAE).
 * 
 * Directly mirrors Angular monorepo authorization directives:
 * - *hasPermission="'course.add'"       -> data-has-permission="course.add"
 * - *hasNotPermission="'course.add'"    -> data-has-not-permission="course.add"
 * - *isUserRole="'admin'"               -> data-is-user-role="admin"
 * - [data-auth-action="remove|disable|hide"] (default: remove, like Angular ViewContainerRef.clear())
 */

import { AuthService } from '../services/AuthService';

export interface AuthCheckConfig {
  permission?: string | string[];
  notPermission?: string | string[];
  role?: string | string[];
  notRole?: string | string[];
  requireAuth?: boolean;
}

/**
 * Programmatic access check function.
 */
export function canAccess(config: AuthCheckConfig): boolean {
  const auth = AuthService.getInstance();

  if (config.requireAuth && !auth.isAuthenticated()) {
    return false;
  }

  if (config.permission !== undefined && config.permission !== null) {
    if (!auth.hasPermission(config.permission)) {
      return false;
    }
  }

  if (config.notPermission !== undefined && config.notPermission !== null) {
    if (auth.hasPermission(config.notPermission)) {
      return false;
    }
  }

  if (config.role !== undefined && config.role !== null) {
    if (!auth.isUserRole(config.role)) {
      return false;
    }
  }

  if (config.notRole !== undefined && config.notRole !== null) {
    if (auth.isUserRole(config.notRole)) {
      return false;
    }
  }

  return true;
}

/**
 * Evaluates and applies declarative authorization directives across the DOM tree.
 * 
 * Supported attributes:
 * - data-has-permission="perm1, perm2"   (comma-separated, OR logic)
 * - data-has-not-permission="perm1"
 * - data-is-user-role="admin, teacher"   (comma-separated, OR logic)
 * - data-is-not-user-role="student"
 * - data-auth-required="true"
 * - data-auth-action="remove | disable | hide" (default: "remove")
 */
export function applyAuthDirectives(root: HTMLElement | Document = document): void {
  const selector = '[data-has-permission], [data-has-not-permission], [data-is-user-role], [data-is-not-user-role], [data-auth-required]';
  const elements = Array.from(root.querySelectorAll<HTMLElement>(selector));

  for (const el of elements) {
    const rawHasPerm = el.getAttribute('data-has-permission');
    const rawHasNotPerm = el.getAttribute('data-has-not-permission');
    const rawIsRole = el.getAttribute('data-is-user-role');
    const rawIsNotRole = el.getAttribute('data-is-not-user-role');
    const rawAuthReq = el.getAttribute('data-auth-required');
    const action = (el.getAttribute('data-auth-action') || 'remove').toLowerCase();

    const config: AuthCheckConfig = {};

    if (rawAuthReq === 'true' || rawAuthReq === '') {
      config.requireAuth = true;
    }

    if (rawHasPerm !== null) {
      const perms = rawHasPerm.split(',').map(s => s.trim()).filter(Boolean);
      config.permission = perms.length === 1 ? perms[0] : perms;
    }

    if (rawHasNotPerm !== null) {
      const perms = rawHasNotPerm.split(',').map(s => s.trim()).filter(Boolean);
      config.notPermission = perms.length === 1 ? perms[0] : perms;
    }

    if (rawIsRole !== null) {
      const roles = rawIsRole.split(',').map(s => s.trim()).filter(Boolean);
      config.role = roles.length === 1 ? roles[0] : roles;
    }

    if (rawIsNotRole !== null) {
      const roles = rawIsNotRole.split(',').map(s => s.trim()).filter(Boolean);
      config.notRole = roles.length === 1 ? roles[0] : roles;
    }

    const isAllowed = canAccess(config);

    if (!isAllowed) {
      if (action === 'disable') {
        el.setAttribute('disabled', 'true');
        el.setAttribute('aria-disabled', 'true');
        el.style.pointerEvents = 'none';
        el.style.opacity = '0.45';
        el.classList.add('auth-disabled');
      } else if (action === 'hide') {
        el.style.display = 'none';
        el.classList.add('auth-hidden');
      } else {
        // Default: remove from DOM (matching Angular's ViewContainerRef.clear())
        el.remove();
      }
    } else {
      if (action === 'disable' && el.classList.contains('auth-disabled')) {
        el.removeAttribute('disabled');
        el.removeAttribute('aria-disabled');
        el.style.pointerEvents = '';
        el.style.opacity = '';
        el.classList.remove('auth-disabled');
      } else if (action === 'hide' && el.classList.contains('auth-hidden')) {
        el.style.display = '';
        el.classList.remove('auth-hidden');
      }
    }
  }
}

/**
 * Initializes a global listener and MutationObserver to automatically keep
 * DOM authorization in sync with reactive auth state and dynamic DOM changes.
 */
export function initAuthDirectivesObserver(root: HTMLElement = document.body): () => void {
  // Apply initially
  applyAuthDirectives(root);

  // Re-apply on auth state changes
  const unsubscribeAuth = AuthService.getInstance().onAuthChange(() => {
    applyAuthDirectives(root);
  });

  // Watch for dynamic DOM injection
  let timeoutId: any = null;
  const observer = new MutationObserver(() => {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      applyAuthDirectives(root);
    }, 50);
  });

  observer.observe(root, { childList: true, subtree: true });

  return () => {
    unsubscribeAuth();
    observer.disconnect();
    if (timeoutId) clearTimeout(timeoutId);
  };
}
